/**
 * Quick reset / grounding techniques — alternatives to breathing exercises.
 * Ported from the standalone Calm The Body app (quickResets.js).
 *
 * Header images live under /public/quick-resets — filenames match the
 * technique id (e.g. sensory.webp for the sensory reset).
 */

export interface Technique {
  id: string;
  image: string;
  title: string;
  desc: string;
  steps: string[];
  guidance: string;
  expectation: string;
}

const qrImg = (id: string): string => `/quick-resets/${id}.webp`;

export const TECHNIQUES: Record<string, Technique> = {
  sensory: {
    id: 'sensory',
    image: qrImg('sensory'),
    title: '5–4–3–2–1 sensory reset',
    desc: 'Reconnect with your senses',
    steps: [
      '5 things you can see',
      '4 things you can feel',
      '3 things you can hear',
      '2 things you can smell',
      '1 thing you can taste',
    ],
    guidance: 'Go slowly. Name each one out loud or silently.',
    expectation:
      'This exercise gently pulls your attention out of your head and back into your body and surroundings. It works by engaging your senses one at a time.',
  },
  cold: {
    id: 'cold',
    image: qrImg('cold'),
    title: 'Cold water reset',
    desc: 'Interrupt overwhelm quickly',
    steps: ['Splash cold water on your face', 'or hold something cold on your cheeks or neck'],
    guidance: "This may feel intense at first — that's expected.",
    expectation:
      'Cold water activates a natural calming reflex in your body. A quick, sharp sensation can interrupt the flood of overwhelm.',
  },
  ground: {
    id: 'ground',
    image: qrImg('ground'),
    title: 'Feet on the ground',
    desc: 'Feel support under you',
    steps: [
      'Place both feet firmly on the floor',
      'Press gently and notice the contact',
      'Feel the surface supporting you',
    ],
    guidance: 'You are supported right now.',
    expectation:
      'Pressing into the floor gives your body a clear signal of support. It helps you feel more stable and here.',
  },
  name: {
    id: 'name',
    image: qrImg('name'),
    title: 'Name the moment',
    desc: 'Orient to the present',
    steps: ['Where you are', 'What time of day it is', 'What you were doing before this'],
    guidance: "I'm here. This is now.",
    expectation:
      "Naming simple facts about the present moment helps orient your mind. It's a way of telling your brain: this is now, not then.",
  },
  exhale: {
    id: 'exhale',
    image: qrImg('exhale'),
    title: 'Long exhale breathing',
    desc: 'Slow your system down',
    steps: [
      'Inhale through your nose',
      'Exhale slowly through pursed lips',
      'Make the out-breath twice as long as the in-breath',
    ],
    guidance: 'Let the exhale do the work.',
    expectation:
      'A longer exhale activates a calming reflex in your nervous system. Even a few breaths can begin to shift things.',
  },
  muscle: {
    id: 'muscle',
    image: qrImg('muscle'),
    title: 'Progressive muscle release',
    desc: 'Release held tension',
    steps: [
      'Gently tense one area — hands, shoulders, or jaw',
      'Hold for 5 seconds',
      'Release and notice the difference',
    ],
    guidance: 'Start with wherever feels tightest.',
    expectation:
      'Tensing and releasing helps your body recognise and let go of hidden tension. It brings awareness back into your body.',
  },
  movement: {
    id: 'movement',
    image: qrImg('movement'),
    title: 'Gentle movement',
    desc: 'Help your body shift',
    steps: ['Slow walking, or rock gently in place', 'Stretch your arms or roll your shoulders'],
    guidance: 'Movement gives your body options.',
    expectation:
      'Small movements remind your body it is not stuck. They help shift energy and bring you back into the present.',
  },
  touch: {
    id: 'touch',
    image: qrImg('touch'),
    title: 'Self-soothing touch',
    desc: 'Use touch to signal safety',
    steps: ['Place a hand on your chest', 'Or give yourself a gentle hug', 'Or hold something warm'],
    guidance: 'Let the warmth register.',
    expectation:
      "Gentle touch can signal safety to your nervous system. It's a simple way to steady yourself physically.",
  },
  handpress: {
    id: 'handpress',
    image: qrImg('handpress'),
    title: 'Hand press',
    desc: 'Ground through pressure',
    steps: ['Press your palms together', 'Hold firmly for 10 seconds', 'Release and notice the sensation'],
    guidance: 'Feel the pressure.',
    expectation:
      'Firm pressure gives your body something solid to register. It can help anchor you when things feel unsteady.',
  },
  orienting: {
    id: 'orienting',
    image: qrImg('orienting'),
    title: 'Orienting look-around',
    desc: 'Reconnect with your environment',
    steps: ['Slowly look around the room', 'Name objects you see', 'Notice colours, textures, light'],
    guidance: 'Take your time. There is no rush.',
    expectation:
      'Looking around slowly tells your brain that your environment is safe. It helps shift you out of internal overwhelm and into the present.',
  },
  jaw: {
    id: 'jaw',
    image: qrImg('jaw'),
    title: 'Jaw release',
    desc: 'Release subtle tension',
    steps: [
      'Let your jaw unclench',
      'Gently open your mouth slightly',
      'Let your tongue rest on the bottom of your mouth',
    ],
    guidance: 'Softening here can calm your system.',
    expectation:
      'The jaw holds more tension than most of us realise. Releasing it can send a calming signal through your whole body.',
  },
};

export interface StateTechniqueGroup {
  primary: string[];
  secondary: string[];
  avoid: string[];
}

/** Which techniques to show for each emotional state, in priority order. */
export const STATE_TECHNIQUES: Record<string, StateTechniqueGroup> = {
  panic: { primary: ['exhale', 'handpress'], secondary: ['sensory', 'ground'], avoid: ['orienting'] },
  overwhelm: { primary: ['sensory', 'name', 'ground'], secondary: ['movement', 'touch'], avoid: [] },
  edgy: { primary: ['exhale', 'muscle', 'movement'], secondary: ['touch', 'jaw'], avoid: ['cold'] },
  rumination: { primary: ['sensory', 'orienting', 'handpress'], secondary: ['movement', 'name'], avoid: [] },
  cant_sleep: { primary: ['exhale', 'muscle', 'jaw'], secondary: ['touch', 'name'], avoid: ['cold', 'movement'] },
  dissociated: { primary: ['ground', 'cold', 'movement'], secondary: ['sensory', 'handpress'], avoid: [] },
  flat: { primary: ['cold', 'movement', 'muscle'], secondary: ['handpress', 'sensory'], avoid: [] },
};

/**
 * Ordered techniques for a state: primary first, then secondary.
 * Unknown ids and unknown states resolve to an empty list.
 */
export function techniquesForState(stateId: string): Technique[] {
  const group = STATE_TECHNIQUES[stateId];
  if (!group) return [];
  const ordered = [...group.primary, ...group.secondary];
  const out: Technique[] = [];
  for (const id of ordered) {
    const t = TECHNIQUES[id];
    if (t) out.push(t);
  }
  return out;
}
